import React from "react";

export type AppAppearanceMode = "system" | "light" | "dark";
export type AppResolvedAppearance = "light" | "dark";

const APP_APPEARANCE_STORAGE_KEY = "synktribe.app.appearance";

interface AppAppearanceContextValue {
  mode: AppAppearanceMode;
  resolvedAppearance: AppResolvedAppearance;
  setMode: (mode: AppAppearanceMode) => void;
}

const AppAppearanceContext = React.createContext<AppAppearanceContextValue | null>(null);

function getSystemAppearance(): AppResolvedAppearance {
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches
  ) {
    return "light";
  }

  return "dark";
}

function readStoredAppearanceMode(): AppAppearanceMode {
  if (typeof window === "undefined") {
    return "system";
  }

  try {
    const stored = window.localStorage.getItem(APP_APPEARANCE_STORAGE_KEY);

    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // no-op
  }

  return "system";
}

function resolveAppearance(mode: AppAppearanceMode): AppResolvedAppearance {
  if (mode === "light" || mode === "dark") {
    return mode;
  }

  return getSystemAppearance();
}

export function AppAppearanceProvider(props: { children: React.ReactNode }): JSX.Element {
  const { children } = props;
  const [mode, setMode] = React.useState<AppAppearanceMode>(() => readStoredAppearanceMode());
  const [resolvedAppearance, setResolvedAppearance] = React.useState<AppResolvedAppearance>(() =>
    resolveAppearance(readStoredAppearanceMode()),
  );

  React.useEffect(() => {
    setResolvedAppearance(resolveAppearance(mode));
  }, [mode]);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
    const handleChange = () => {
      if (mode === "system") {
        setResolvedAppearance(mediaQuery.matches ? "light" : "dark");
      }
    };

    handleChange();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);

      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    }

    mediaQuery.addListener(handleChange);

    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, [mode]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(APP_APPEARANCE_STORAGE_KEY, mode);
    } catch {
      // no-op
    }
  }, [mode]);

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const root = document.documentElement;
    root.setAttribute("data-appearance", mode);
    root.setAttribute("data-theme", resolvedAppearance);
    root.classList.toggle("dark", resolvedAppearance === "dark");
    root.style.colorScheme = resolvedAppearance;

    return () => {
      root.removeAttribute("data-appearance");
      root.removeAttribute("data-theme");
      root.classList.remove("dark");
      root.style.removeProperty("color-scheme");
    };
  }, [mode, resolvedAppearance]);

  const value = React.useMemo<AppAppearanceContextValue>(
    () => ({
      mode,
      resolvedAppearance,
      setMode,
    }),
    [mode, resolvedAppearance],
  );

  return <AppAppearanceContext.Provider value={value}>{children}</AppAppearanceContext.Provider>;
}

export function useAppAppearance(): AppAppearanceContextValue {
  const context = React.useContext(AppAppearanceContext);

  if (!context) {
    throw new Error("useAppAppearance must be used inside AppAppearanceProvider");
  }

  return context;
}

export function useOptionalAppAppearance(): AppAppearanceContextValue | null {
  return React.useContext(AppAppearanceContext);
}